import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { OnboardingProgress, OnboardingStatus, OnboardingSection } from '@/types/onboarding';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Circle,
  Building2,
  CreditCard,
  Printer,
  Globe,
  Share2,
  Star,
  Package,
  Trophy,
  Target,
  Zap,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface OnboardingHeaderProps {
  progress: OnboardingProgress;
}

const SECTION_CONFIGS = {
  [OnboardingSection.BUSINESS_DETAILS]: {
    label: 'Business Details',
    icon: Building2,
    color: 'blue'
  },
  [OnboardingSection.STRIPE_CONNECT]: {
    label: 'Payment Setup',
    icon: CreditCard,
    color: 'purple'
  },
  [OnboardingSection.PRINTER]: {
    label: 'Printer',
    icon: Printer,
    color: 'green'
  },
  [OnboardingSection.DOMAIN]: {
    label: 'Custom Domain',
    icon: Globe,
    color: 'cyan'
  },
  [OnboardingSection.WEBSITE]: {
    label: 'Website',
    icon: Share2,
    color: 'orange'
  },
  [OnboardingSection.GOOGLE_BUSINESS]: {
    label: 'Google Business',
    icon: Star,
    color: 'yellow'
  },
  [OnboardingSection.AGGREGATORS]: {
    label: 'Aggregators',
    icon: Package,
    color: 'red'
  },
};

const getStatusIcon = (status: OnboardingStatus) => {
  switch (status) {
    case OnboardingStatus.COMPLETE:
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    case OnboardingStatus.AWAITING_CONFIRMATION:
      return <Clock className="h-5 w-5 text-yellow-600" />;
    case OnboardingStatus.NEEDS_ATTENTION:
      return <AlertCircle className="h-5 w-5 text-red-600" />;
    default:
      return <Circle className="h-5 w-5 text-gray-400" />;
  }
};

const getStatusColor = (status: OnboardingStatus) => {
  switch (status) {
    case OnboardingStatus.COMPLETE:
      return 'border-green-200 bg-green-50';
    case OnboardingStatus.AWAITING_CONFIRMATION:
      return 'border-yellow-200 bg-yellow-50';
    case OnboardingStatus.NEEDS_ATTENTION:
      return 'border-red-200 bg-red-50';
    default:
      return 'border-gray-200 bg-gray-50';
  }
};

const getProgressMessage = (percentage: number) => {
  if (percentage === 100) {
    return {
      message: "🎉 Congratulations! Your onboarding is complete!",
      description: "All sections have been finished. Our team will review and activate your account soon.",
      icon: Trophy,
      color: "text-green-600"
    };
  } else if (percentage >= 75) {
    return {
      message: "🚀 Almost there! You're in the final stretch!",
      description: "Just a few more sections to go before your restaurant is ready to take orders.",
      icon: Target,
      color: "text-blue-600"
    };
  } else if (percentage >= 50) {
    return {
      message: "⚡ Great progress! You're halfway done!",
      description: "Keep going! You're making excellent progress on your setup.",
      icon: Zap,
      color: "text-purple-600"
    };
  } else if (percentage >= 25) {
    return {
      message: "🌟 Good start! You're on your way!",
      description: "Nice work getting started. Continue filling out the remaining sections.",
      icon: Target,
      color: "text-orange-600"
    };
  } else {
    return {
      message: "👋 Welcome to Pump'd! Let's get started!",
      description: "Complete each section to set up your restaurant for online ordering.",
      icon: Building2,
      color: "text-gray-600"
    };
  }
};

const OnboardingHeader: React.FC<OnboardingHeaderProps> = ({ progress }) => {
  const progressInfo = getProgressMessage(progress.progressPercentage);
  const ProgressIcon = progressInfo.icon;

  // Define the display order for sections
  const sectionOrder = [
    OnboardingSection.BUSINESS_DETAILS,
    OnboardingSection.STRIPE_CONNECT,
    OnboardingSection.DOMAIN,
    OnboardingSection.WEBSITE,
    OnboardingSection.GOOGLE_BUSINESS,
    OnboardingSection.AGGREGATORS,
    OnboardingSection.PRINTER,
  ];

  // Sort sections according to the defined order
  const sortedSections = [...progress.sections].sort((a, b) => {
    const aIndex = sectionOrder.indexOf(a.section);
    const bIndex = sectionOrder.indexOf(b.section);
    return aIndex - bIndex;
  });

  return (
    <div className="space-y-6">
      {/* Welcome Message and Overall Progress */}
      <Card className="bg-gradient-to-r from-brand-blue-light-3 to-brand-yellow-light-2">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <ProgressIcon className={cn("h-8 w-8", progressInfo.color)} />
            <div className="flex-1">
              <CardTitle className="text-xl">{progressInfo.message}</CardTitle>
              <CardDescription className="text-base mt-1">
                {progressInfo.description}
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-brand-blue">{progress.progressPercentage}%</div>
              <div className="text-sm text-muted-foreground">Complete</div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Progress value={progress.progressPercentage} className="h-3" />
          <div className="flex justify-between items-center text-sm text-muted-foreground mt-2">
            <span>{progress.completedSections} of {progress.totalSections} sections complete</span>
            <div className="flex items-center gap-2">
              <span>{progress.totalSections - progress.completedSections} remaining</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-auto p-1">
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  className="w-screen max-w-4xl p-0" 
                  align="end" 
                  side="bottom"
                  sideOffset={8}
                >
                  <div className="p-4">
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold">Section Progress Details</h3>
                      <p className="text-sm text-muted-foreground">
                        Track your progress through each onboarding section.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {sortedSections.map((sectionProgress) => {
                        const config = SECTION_CONFIGS[sectionProgress.section];
                        const Icon = config.icon;
                        
                        return (
                          <div 
                            key={sectionProgress.section}
                            className={cn(
                              "p-3 rounded-lg border-2 transition-all duration-200",
                              getStatusColor(sectionProgress.status)
                            )}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className={cn(
                                "p-1.5 rounded-md flex-shrink-0",
                                sectionProgress.status === OnboardingStatus.COMPLETE ? "bg-green-100" :
                                sectionProgress.status === OnboardingStatus.AWAITING_CONFIRMATION ? "bg-yellow-100" :
                                sectionProgress.status === OnboardingStatus.NEEDS_ATTENTION ? "bg-red-100" :
                                "bg-gray-100"
                              )}>
                                <Icon className={cn(
                                  "h-4 w-4",
                                  sectionProgress.status === OnboardingStatus.COMPLETE ? "text-green-600" :
                                  sectionProgress.status === OnboardingStatus.AWAITING_CONFIRMATION ? "text-yellow-600" :
                                  sectionProgress.status === OnboardingStatus.NEEDS_ATTENTION ? "text-red-600" :
                                  "text-gray-500"
                                )} />
                              </div>
                              <div className="flex-shrink-0">
                                {getStatusIcon(sectionProgress.status)}
                              </div>
                            </div>
                            
                            <div className="space-y-1.5">
                              <h4 className="font-medium text-sm leading-tight">{config.label}</h4>
                              
                              <Badge 
                                variant={
                                  sectionProgress.status === OnboardingStatus.COMPLETE ? 'default' :
                                  sectionProgress.status === OnboardingStatus.AWAITING_CONFIRMATION ? 'secondary' :
                                  sectionProgress.status === OnboardingStatus.NEEDS_ATTENTION ? 'destructive' :
                                  'outline'
                                }
                                className="text-[10px] px-1.5 py-0.5"
                              >
                                {sectionProgress.status === OnboardingStatus.NOT_STARTED && 'Not Started'}
                                {sectionProgress.status === OnboardingStatus.AWAITING_CONFIRMATION && 'Pending Review'}
                                {sectionProgress.status === OnboardingStatus.NEEDS_ATTENTION && 'Needs Attention'}
                                {sectionProgress.status === OnboardingStatus.COMPLETE && 'Complete'}
                              </Badge>

                              {sectionProgress.isRequired && !sectionProgress.hasRequiredFields && (
                                <div className="text-[10px] text-muted-foreground leading-tight">
                                  Required fields needed
                                </div>
                              )}

                              {sectionProgress.validationErrors && sectionProgress.validationErrors.length > 0 && (
                                <div className="text-[10px] text-red-600 leading-tight">
                                  <div className="font-medium">Issues:</div>
                                  <ul className="list-disc list-inside space-y-0.5">
                                    {sectionProgress.validationErrors.slice(0, 1).map((error, index) => (
                                      <li key={index} className="truncate">{error}</li>
                                    ))}
                                    {sectionProgress.validationErrors.length > 1 && (
                                      <li className="text-muted-foreground">+{sectionProgress.validationErrors.length - 1} more</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                              
                              {sectionProgress.hasData && sectionProgress.status === OnboardingStatus.NOT_STARTED && (
                                <div className="text-[10px] text-blue-600 font-medium">
                                  Draft saved
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {progress.sections.filter(s => s.status === OnboardingStatus.COMPLETE).length}
              </div>
              <div className="text-sm text-muted-foreground">Complete</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">
                {progress.sections.filter(s => s.status === OnboardingStatus.AWAITING_CONFIRMATION).length}
              </div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {progress.sections.filter(s => s.status === OnboardingStatus.NEEDS_ATTENTION).length}
              </div>
              <div className="text-sm text-muted-foreground">Attention</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Circle className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-600">
                {progress.sections.filter(s => s.status === OnboardingStatus.NOT_STARTED).length}
              </div>
              <div className="text-sm text-muted-foreground">Not Started</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingHeader;